"use client"
import type { HeroHeaderView } from "@opensea/navigation"
import type { MediaFormat } from "@opensea/seadn"
import { classNames, tv, useIsLessThanLg } from "@opensea/ui-kit"
import type { Nullable } from "@opensea/utils"
import { BannerMedia } from "@/components/media/BannerMedia"
import { HeroBannerMediaGradientOverlay } from "./HeroBannerMediaGradientOverlay"

const transitionClassNames = classNames(
  "transition-[opacity] duration-1000 ease-out-quint",
)

const baseClassNames = classNames("absolute inset-0", transitionClassNames)

const heroBannerMediaVariants = tv({
  base: classNames(baseClassNames, "top-0 size-full overflow-hidden"),
  variants: {
    headerView: {
      full: "opacity-100",
      compact:
        "h-[calc(theme(spacing.sm-top-nav)+theme(spacing.hero-header-compact))] opacity-0 lg:h-[calc(theme(spacing.lg-top-nav)+theme(spacing.hero-header-compact))]",
    },
    blur: {
      true: "-translate-y-5 invisible scale-110 opacity-20 blur-xl dark:visible",
    },
  },
})

type Props = {
  mediaUrl: Nullable<string>
  fallbackImageUrl?: Nullable<string>
  blur?: boolean
  className?: string
  headerView: HeroHeaderView
  mediaFormat?: MediaFormat
}

export function HeroBannerMedia({
  blur,
  headerView,
  className,
  mediaUrl,
  fallbackImageUrl,
  mediaFormat,
}: Props) {
  const isLessThanLg = useIsLessThanLg()

  return (
    <div
      className={classNames(
        heroBannerMediaVariants({ headerView, blur }),
        className,
      )}
    >
      <BannerMedia
        className="inset-0"
        disableVideo={headerView !== "full"}
        fallback={fallbackImageUrl}
        format={mediaFormat}
        overlay={<HeroBannerMediaGradientOverlay headerView={headerView} />}
        priority
        url={mediaUrl}
        width={isLessThanLg ? 2000 : 3000}
      />
      {blur ? (
        <div
          className={classNames(
            baseClassNames,
            transitionClassNames,
            "backdrop-blur-3xl",
          )}
        />
      ) : null}
    </div>
  )
}
